import React, { createContext, useContext, useEffect, useState } from 'react';
import { fetchMe, getStoredUser, getToken, clearToken, setStoredUser } from '../lib/auth';

const AuthContext = createContext(null);

/**
 * Provides { user, setUser, logout, authLoaded } to the tree.
 *
 * On mount, attempts to hydrate user from /api/me (token in localStorage).
 * Falls back to cached user object if the API is unreachable.
 * authLoaded becomes true once the initial fetch resolves.
 */
export function AuthProvider({ children }) {
  const [user, setUserState] = useState(() => getStoredUser());
  const [authLoaded, setAuthLoaded] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      // No token at all — clear any stale cached user immediately
      setUserState(null);
      setAuthLoaded(true);
      return;
    }
    fetchMe().then((u) => {
      if (u) {
        setUserState(u);
        setStoredUser(u);
      } else if (u === false) {
        // Token explicitly rejected by backend — clear everything
        clearToken();
        setUserState(null);
      }
      // u === null means network error — keep existing state (offline resilience)
      setAuthLoaded(true);
    });
  }, []);

  const setUser = (u) => { setUserState(u); if (u) setStoredUser(u); };
  const logout  = ()  => { clearToken(); setUserState(null); };

  const refreshUser = async () => {
    if (!getToken()) return;
    const u = await fetchMe();
    if (u) { setUserState(u); setStoredUser(u); }
    else if (u === false) { clearToken(); setUserState(null); }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, logout, authLoaded, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const apiClient = {
  get: async (url, config = {}) => {
    const token = localStorage.getItem("bm_auth_token");
    const res = await fetch(`/api${url}`, {
      ...config,
      headers: {
        ...(config.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || data.message || "Request failed");
    return { data };
  },
  post: async (url, body, config = {}) => {
    const token = localStorage.getItem("bm_auth_token");
    const res = await fetch(`/api${url}`, {
      method: "POST",
      ...config,
      headers: {
        "Content-Type": "application/json",
        ...(config.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body || {}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || data.message || "Request failed");
    return { data };
  },
};
